import apiClient from './client';
import type {
  Recipe, RecipeWithDetails, RecipeType,
  PaginatedResponse, SingleResponse,
  AppSetting, RecipeCalculationResult,
} from '@/types';

export interface RecipeListParams {
  page?:       number;
  limit?:      number;
  search?:     string;
  activeOnly?: boolean;
  typeId?:     string;
}

export interface RecipePayload {
  name:               string;
  recipe_type_id?:    string | null;
  portion_weight_g:   number;
  multiplier:         number;
  overrun_pct:        number;
  tax_rate:           number;
  desired_margin:     number;
  selling_price?:     number | null;
  prep_time_min?:     number | null;
  cook_time_min?:     number | null;
  preparation_notes?: string | null;
  presentation_notes?: string | null;
  kitchen_notes?:     string | null;
  is_active?:         boolean;
  ingredients?: Array<{
    ingredient_id:  string;
    grams_per_kilo: number;
    waste_ratio:    number;
    sort_order:     number;
  }>;
}

export interface PreviewPayload {
  portion_weight_g: number;
  multiplier:       number;
  overrun_pct:      number;
  tax_rate:         number;
  desired_margin:   number;
  selling_price?:   number | null;
  ingredients:      Array<{ ingredient_id: string; grams_per_kilo: number; waste_ratio: number }>;
}

export const recipesApi = {
  list: (params: RecipeListParams = {}) =>
    apiClient.get<PaginatedResponse<Recipe>>('/recipes', { params }).then(r => r.data),

  get: (id: string) =>
    apiClient.get<SingleResponse<RecipeWithDetails>>(`/recipes/${id}`).then(r => r.data.data),

  create: (data: RecipePayload) =>
    apiClient.post<SingleResponse<RecipeWithDetails>>('/recipes', data).then(r => r.data.data),

  update: (id: string, data: RecipePayload) =>
    apiClient.put<SingleResponse<RecipeWithDetails>>(`/recipes/${id}`, data).then(r => r.data.data),

  delete: (id: string) =>
    apiClient.delete(`/recipes/${id}`).then(r => r.data),

  calculate: (id: string) =>
    apiClient.post<SingleResponse<RecipeCalculationResult>>(`/recipes/${id}/calculate`).then(r => r.data.data),

  preview: (payload: PreviewPayload) =>
    apiClient.post<SingleResponse<RecipeCalculationResult>>('/recipes/preview', payload).then(r => r.data.data),

  // Recipe types
  listTypes: () =>
    apiClient.get<SingleResponse<RecipeType[]>>('/recipes/types').then(r => r.data.data),

  createType: (name: string) =>
    apiClient.post<SingleResponse<RecipeType>>('/recipes/types', { name }).then(r => r.data.data),

  // Settings
  listSettings: () =>
    apiClient.get<SingleResponse<AppSetting[]>>('/recipes/settings').then(r => r.data.data),

  getSettings: () =>
    apiClient.get<SingleResponse<AppSetting[]>>('/recipes/settings').then(r => r.data.data),

  updateSetting: (key: string, value: string) =>
    apiClient.put(`/recipes/settings/${key}`, { value }).then(r => r.data),
};
